import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  IconButton,
  Divider,
  Paper,
  Collapse,
  Popper,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Add,
  Home,
  ViewQuilt,
  MoreVert,
  DragIndicator,
  Article,
  Delete,
  ContentCopy,
  ArrowUpward,
  ArrowDownward,
  Visibility,
  VisibilityOff,
  Edit,
  SubdirectoryArrowRight,
  HomeFilled,
  Description,
  DeleteOutline,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { Config } from '../../../App';
import { PageConfig, SeoConfig } from '../../../pages/types';
import SeoEditor from './SeoEditor';
import { availableComponents } from '../../../pages/dynamic-page';
import { cleanComponentData } from '../../../utils/component-utils';
import { SxProps, Theme } from '@mui/material';
import defaultTheme from '../../../theme';
import ConfirmDialog from '../../confirm-dialog';

interface PageEditorProps {
  config: Config | null;
  onConfigChange: (newConfig: Config) => void;
  onOpenFileSelector?: (callback: (url: string) => void) => void;
  isAdmin: boolean;
}

const PageEditor: React.FC<PageEditorProps> = ({
  config,
  onConfigChange,
  onOpenFileSelector,
  isAdmin,
}) => {
  const [expandedPages, setExpandedPages] = useState<Record<string, boolean>>(
    {},
  );
  const [pagesExpanded, setPagesExpanded] = useState(true);
  const [addContentExpanded, setAddContentExpanded] = useState(true);
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [selectedPageKey, setSelectedPageKey] = useState<string | null>(null);
  const [selectedComponentPath, setSelectedComponentPath] = useState<
    (string | number)[] | null
  >(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pageMenuAnchorEl, setPageMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [targetPageKey, setTargetPageKey] = useState<string | null>(null);

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmContent, setConfirmContent] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const handleSeoChange = (seo: SeoConfig) => {
    if (!config || !selectedPageKey) return;
    const newConfig = JSON.parse(JSON.stringify(config));
    const findPage = (pages: any): any => {
      if (pages[selectedPageKey]) return pages[selectedPageKey];
      for (const k in pages) {
        if (pages[k].subpages) {
          const found = findPage(pages[k].subpages);
          if (found) return found;
        }
      }
      return null;
    };
    const page = findPage(newConfig.pages);
    if (page) {
      page.seo = seo;
      onConfigChange(newConfig);
    }
  };

  const requestConfirm = (
    title: string,
    content: string,
    action: () => void,
  ) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  const handlePageMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    key: string,
  ) => {
    event.stopPropagation();
    setPageMenuAnchorEl(event.currentTarget);
    setTargetPageKey(key);
  };

  const handlePageMenuClose = () => {
    setPageMenuAnchorEl(null);
    setTargetPageKey(null);
  };

  const handlePageVisibilityToggle = (
    e: React.MouseEvent,
    key: string,
    currentStatus: boolean,
  ) => {
    e.stopPropagation();
    if (!config) return;
    const newConfig = JSON.parse(JSON.stringify(config));

    // Helper to find page
    const findPage = (pages: any): any => {
      if (pages[key]) return pages[key];
      for (const k in pages) {
        if (pages[k].subpages) {
          const found = findPage(pages[k].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const page = findPage(newConfig.pages);
    if (page) {
      page.showInNavigation = !currentStatus;
      onConfigChange(newConfig);
    }
  };

  const handlePageMove = (direction: 'up' | 'down') => {
    if (!config || !targetPageKey) return;

    const newConfig = JSON.parse(JSON.stringify(config));

    // Find parent object containing the target page
    const findPageAndParent = (
      pages: any,
    ): { parent: any; key: string } | null => {
      if (pages[targetPageKey]) return { parent: pages, key: targetPageKey };
      for (const k in pages) {
        if (pages[k].subpages) {
          const found = findPageAndParent(pages[k].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const result = findPageAndParent(newConfig.pages);
    if (result) {
      const { parent, key } = result;
      const entries = Object.entries(parent);
      const index = entries.findIndex(([k]) => k === key);

      if (direction === 'up' && index > 0) {
        // Swap with previous
        const temp = entries[index];
        entries[index] = entries[index - 1];
        entries[index - 1] = temp;
      } else if (direction === 'down' && index < entries.length - 1) {
        // Swap with next
        const temp = entries[index];
        entries[index] = entries[index + 1];
        entries[index + 1] = temp;
      } else {
        handlePageMenuClose();
        return; // Can't move
      }

      // Rebuild parent object
      for (const k in parent) delete parent[k];
      entries.forEach(([k, v]) => (parent[k] = v));

      onConfigChange(newConfig);
    }
    handlePageMenuClose();
  };

  const handlePageRename = () => {
    if (!config || !targetPageKey) return;
    const newName = prompt('Enter new menu title:');
    if (!newName) return;

    const newConfig = JSON.parse(JSON.stringify(config));
    // Helper to find page
    const findPage = (pages: any): any => {
      if (pages[targetPageKey]) return pages[targetPageKey];
      for (const k in pages) {
        if (pages[k].subpages) {
          const found = findPage(pages[k].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const page = findPage(newConfig.pages);
    if (page) {
      page.menutitle = newName;
      onConfigChange(newConfig);
    }
    handlePageMenuClose();
  };

  const handlePageDuplicate = () => {
    if (!config || !targetPageKey) return;

    const newConfig = JSON.parse(JSON.stringify(config));

    const findPageAndParent = (
      pages: any,
    ): { parent: any; key: string } | null => {
      if (pages[targetPageKey]) return { parent: pages, key: targetPageKey };
      for (const k in pages) {
        if (pages[k].subpages) {
          const found = findPageAndParent(pages[k].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const result = findPageAndParent(newConfig.pages);
    if (result) {
      const { parent, key } = result;
      const originalPage = parent[key];
      const newKey = `${key}_copy_${Date.now()}`;
      const newPage = JSON.parse(JSON.stringify(originalPage));
      newPage.menutitle = `${newPage.menutitle} (Copy)`;

      // Insert after original
      const entries = Object.entries(parent);
      const index = entries.findIndex(([k]) => k === key);

      const newEntries = [
        ...entries.slice(0, index + 1),
        [newKey, newPage],
        ...entries.slice(index + 1),
      ];

      // Rebuild parent object in place to preserve order
      for (const k in parent) delete parent[k];
      newEntries.forEach(([k, v]) => (parent[k] = v));

      onConfigChange(newConfig);
    }
    handlePageMenuClose();
  };

  const handlePageRemove = () => {
    if (!config || !targetPageKey) return;

    requestConfirm(
      'Delete Page?',
      'Are you sure you want to delete this page? This action cannot be undone.',
      () => {
        if (!config || !targetPageKey) return;
        const newConfig = JSON.parse(JSON.stringify(config));

        const findPageAndParent = (
          pages: any,
        ): { parent: any; key: string } | null => {
          if (pages[targetPageKey])
            return { parent: pages, key: targetPageKey };
          for (const k in pages) {
            if (pages[k].subpages) {
              const found = findPageAndParent(pages[k].subpages);
              if (found) return found;
            }
          }
          return null;
        };

        const result = findPageAndParent(newConfig.pages);
        if (result) {
          const { parent, key } = result;
          delete parent[key];
          onConfigChange(newConfig);

          if (selectedPageKey === targetPageKey) {
            setSelectedPageKey(null);
            setSelectedComponentPath(null);
          }
        }
      },
    );

    handlePageMenuClose();
  };

  // Helper to get the current page config
  const getCurrentPageConfig = (): PageConfig | null => {
    if (!config || !selectedPageKey) return null;

    // Handle nested pages if necessary, but for now assume flat or simple structure
    // The previous implementation passed the full page object, so let's try to find it
    // We need to traverse the pages object to find the key
    const findPage = (
      pages: Record<string, PageConfig>,
    ): PageConfig | undefined => {
      if (pages[selectedPageKey]) return pages[selectedPageKey];
      for (const key in pages) {
        if (pages[key].subpages) {
          const found = findPage(pages[key].subpages!);
          if (found) return found;
        }
      }
      return undefined;
    };

    return findPage(config.pages) || null;
  };

  const selectedPageConfig = getCurrentPageConfig();

  // Create the site theme for the preview area
  const siteTheme = React.useMemo(() => {
    if (!config) return createTheme();

    const mergedTheme = deepmerge(defaultTheme, {
      ...config.theme,
      palette: {
        ...config.theme?.palette,
        mode: config.theme?.palette?.mode || 'light',
      },
    });
    return createTheme(mergedTheme);
  }, [config]);

  const createDefaultComponent = (type: string): any => {
    const id = `comp_${Date.now()}`;
    switch (type) {
      case 'HERO':
        return {
          type: 'Hero',
          id,
          props: {
            title: 'New Hero',
            subtitle: 'Subtitle',
            image: { url: '', alt: 'Hero Image' },
          },
          styles: {},
        };
      case 'PAGEHEADER':
        return {
          type: 'PageHeader',
          id,
          props: {
            title: 'Page Header',
            subtitle: 'Subtitle',
          },
          styles: { height: '200px' },
        };
      case 'CONTAINER':
        return {
          type: 'Container',
          id,
          props: {
            container: {
              columns: 1,
              spacing: 2,
              maxWidth: '1200px',
              items: [],
            },
          },
          styles: { padding: '2rem' },
        };
      case 'TEXTBOX':
        return {
          type: 'TextBox',
          id,
          props: {
            texts: [
              { type: 'h3', contents: 'New Text Box' },
              { type: 'paragraph', contents: 'Add your text here.' },
            ],
          },
          styles: { padding: '1rem' },
        };
      case 'IMAGE':
        return {
          type: 'SingleImage',
          id,
          props: {
            image: { url: '', alt: 'New Image' },
            imageWidth: '100%',
          },
          styles: {},
        };
      case 'GALLERIA':
        return {
          type: 'ImageGallery',
          id,
          props: {
            images: [],
            columns: 3,
            gap: '1rem',
          },
          styles: {},
        };
      case 'CAROUSEL':
        return {
          type: 'Carousel',
          id,
          props: {
            images: [],
            carouselHeight: '400px',
          },
          styles: {},
        };
      case 'INGRESS':
        return {
          type: 'Ingress',
          id,
          props: {
            text: 'Ingress text goes here.',
          },
          styles: {},
        };
      case 'SIDETITLE':
        return {
          type: 'TitleParagraph',
          id,
          props: {
            title: 'Side Title',
            paragraph: 'Paragraph text.',
          },
          styles: {},
        };
      case 'IFRAME':
        return {
          type: 'Iframe',
          id,
          props: {
            iframeUrl: '',
            iframeHeight: '400px',
          },
          styles: {},
        };
      default:
        return null;
    }
  };

  const handleAddContent = (type: string) => {
    if (!config || !selectedPageKey) return;

    const newComponent = createDefaultComponent(type);
    if (!newComponent) return;

    const newConfig = JSON.parse(JSON.stringify(config));

    // Helper to find page parent and key in newConfig
    const findPageInConfig = (pages: any): any => {
      if (pages[selectedPageKey]) return pages[selectedPageKey];
      for (const key in pages) {
        if (pages[key].subpages) {
          const found = findPageInConfig(pages[key].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const pageInConfig = findPageInConfig(newConfig.pages);
    if (!pageInConfig) return;

    if (selectedComponentPath) {
      // Insert after selected component
      let targetList = pageInConfig;
      // Navigate to the list containing the selected component
      for (let i = 0; i < selectedComponentPath.length - 1; i++) {
        targetList = targetList[selectedComponentPath[i]];
      }
      const index = selectedComponentPath[
        selectedComponentPath.length - 1
      ] as number;
      targetList.splice(index + 1, 0, newComponent);
    } else {
      // Insert at top of page
      if (!pageInConfig.components) {
        pageInConfig.components = [];
      }
      pageInConfig.components.unshift(newComponent);
    }

    onConfigChange(newConfig);
  };

  const handleComponentAction = (
    action: 'delete' | 'duplicate' | 'moveUp' | 'moveDown',
    path: (string | number)[],
  ) => {
    if (!config || !selectedPageKey) return;

    const newConfig = JSON.parse(JSON.stringify(config));
    let current: any = newConfig.pages;

    // Find the page first
    // This logic needs to match how we find the page in getCurrentPageConfig
    // But since we have the path relative to the page components, we need to find the page object in newConfig

    // Helper to find page parent and key in newConfig
    const findPageInConfig = (pages: any): any => {
      if (pages[selectedPageKey]) return pages[selectedPageKey];
      for (const key in pages) {
        if (pages[key].subpages) {
          const found = findPageInConfig(pages[key].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const pageInConfig = findPageInConfig(newConfig.pages);
    if (!pageInConfig) return;

    // Navigate to the component list
    // path[0] is 'components'
    let targetList = pageInConfig;
    for (let i = 0; i < path.length - 1; i++) {
      targetList = targetList[path[i]];
    }

    const index = path[path.length - 1] as number;

    if (action === 'delete') {
      requestConfirm(
        'Delete Component?',
        'Are you sure you want to delete this component? This action cannot be undone.',
        () => {
          targetList.splice(index, 1);
          setSelectedComponentPath(null);
          onConfigChange(newConfig);
        },
      );
      return; // Do not call onConfigChange immediately
    } else if (action === 'duplicate') {
      const item = JSON.parse(JSON.stringify(targetList[index]));
      // Generate new ID if needed
      if (item.id) item.id = `${item.id}_copy_${Date.now()}`;
      targetList.splice(index + 1, 0, item);
    } else if (action === 'moveUp' && index > 0) {
      const item = targetList[index];
      targetList.splice(index, 1);
      targetList.splice(index - 1, 0, item);
    } else if (action === 'moveDown' && index < targetList.length - 1) {
      const item = targetList[index];
      targetList.splice(index, 1);
      targetList.splice(index + 1, 0, item);
    }

    onConfigChange(newConfig);
  };

  const handlePropsChange = (path: (string | number)[], newProps: any) => {
    if (!config || !selectedPageKey) return;

    const newConfig = JSON.parse(JSON.stringify(config));

    // Helper to find page parent and key in newConfig
    const findPageInConfig = (pages: any): any => {
      if (pages[selectedPageKey]) return pages[selectedPageKey];
      for (const key in pages) {
        if (pages[key].subpages) {
          const found = findPageInConfig(pages[key].subpages);
          if (found) return found;
        }
      }
      return null;
    };

    const pageInConfig = findPageInConfig(newConfig.pages);
    if (!pageInConfig) return;

    // Navigate to the component
    let targetComponent = pageInConfig;
    // path includes 'components', index, 'props', etc.
    // We want to update the 'props' of the component
    // The path passed from renderComponent is the path to the component itself
    // e.g. ['components', 0]

    for (let i = 0; i < path.length; i++) {
      targetComponent = targetComponent[path[i]];
    }

    // Update props
    targetComponent.props = { ...targetComponent.props, ...newProps };

    onConfigChange(newConfig);
  };

  const renderComponent = (
    componentData: any,
    index: number,
    path: (string | number)[] = ['components'],
  ) => {
    const cleanedComponent = cleanComponentData(componentData);
    const component = availableComponents.find(
      (c) => c.type === cleanedComponent.type,
    );
    const backgroundUrl = cleanedComponent.styles?.backgroundImage?.url;

    const currentPath = [...path, index];
    const isSelected =
      selectedComponentPath &&
      JSON.stringify(selectedComponentPath) === JSON.stringify(currentPath);

    if (!component) return null;

    const content = (() => {
      // Special handling for Container component
      if (component.type === 'Container' && cleanedComponent.props?.container) {
        const containerProps = cleanedComponent.props.container;
        return (
          <Box
            sx={{
              width: '100%',
              ...(cleanedComponent.styles as SxProps<Theme>),
              background: backgroundUrl
                ? `url(${backgroundUrl})`
                : cleanedComponent.styles?.background,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              justifyContent: 'center',
              boxSizing: 'border-box',
              margin: '0 auto',
              px: 2,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: containerProps.maxWidth || '1200px',
              }}
            >
              <component.component
                {...cleanedComponent.props}
                id={cleanedComponent.id}
                columns={containerProps.columns}
                spacing={containerProps.spacing}
                items={containerProps.items}
                maxWidth={containerProps.maxWidth}
                minColumns={containerProps.minColumns}
                useTabs={containerProps.useTabs}
                tabs={containerProps.tabs}
                renderComponent={(c: any, i: number, context?: any) => {
                  let suffix = ['props', 'container', 'items'];
                  if (context && context.type === 'tab') {
                    suffix = [
                      'props',
                      'container',
                      'tabs',
                      context.tabIndex,
                      'items',
                    ];
                  }
                  return renderComponent(c, i, [...currentPath, ...suffix]);
                }}
                sx={cleanedComponent.styles}
                isEditable={true}
                onPropsChange={(newProps: any) =>
                  handlePropsChange(currentPath, {
                    container: { ...containerProps, ...newProps },
                  })
                }
                onImageUpload={onOpenFileSelector}
              />
            </Box>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            ...(cleanedComponent.styles as SxProps<Theme>),
            background: backgroundUrl
              ? `url(${backgroundUrl})`
              : cleanedComponent.styles?.background,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxSizing: 'border-box',
          }}
        >
          {React.createElement(component.component, {
            ...cleanedComponent.props,
            id: cleanedComponent.id,
            sx: cleanedComponent.styles,
            isEditable: true,
            onPropsChange: (newProps: any) =>
              handlePropsChange(currentPath, newProps),
            onImageUpload: onOpenFileSelector,
          })}
        </Box>
      );
    })();

    return (
      <Box
        key={index}
        onClick={(e) => {
          if (!isAdmin) return;
          e.stopPropagation();
          setSelectedComponentPath(currentPath);
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          position: 'relative',
          border:
            isSelected && isAdmin
              ? '2px solid #1976d2'
              : '2px solid transparent',
          zIndex: isSelected && isAdmin ? 100 : 1,
          '&:hover': {
            border:
              isSelected && isAdmin
                ? '2px solid #1976d2'
                : isAdmin
                  ? '2px dashed rgba(0, 0, 0, 0.1)'
                  : 'none',
          },
          transition: 'border 0.2s',
        }}
      >
        {content}
      </Box>
    );
  };

  const renderAddContentButton = (label: string) => {
    if (!isAdmin) return null;

    return (
      <Button
        variant="outlined"
        startIcon={<Add />}
        fullWidth
        onClick={() => handleAddContent(label)}
        sx={{
          justifyContent: 'flex-start',
          mb: 1,
          textTransform: 'uppercase',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          borderColor: '#e0e0e0',
          color: '#1976d2',
          '&:hover': {
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
        }}
      >
        {label}
      </Button>
    );
  };

  const renderPageItem = (key: string, page: PageConfig, level: number = 0) => {
    const hasSubpages = page.subpages && Object.keys(page.subpages).length > 0;
    const isHome = key.toLowerCase() === 'home' || key === 'index';
    const isSelected = selectedPageKey === key;
    const isVisible = page.showInNavigation !== false;

    return (
      <React.Fragment key={key}>
        <ListItem
          disablePadding
          secondaryAction={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => handlePageVisibilityToggle(e, key, isVisible)}
                sx={{
                  mr: 0.5,
                  color: isVisible ? 'action.active' : 'text.disabled',
                }}
                title={
                  isVisible ? 'Hide from navigation' : 'Show in navigation'
                }
              >
                {isVisible ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => handlePageMenuOpen(e, key)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <ListItemButton
            sx={{
              pl: 2 + level * 2,
              pr: 9, // Add padding right to avoid overlap with secondary actions
              bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              '&:hover': {
                bgcolor: isSelected
                  ? 'rgba(25, 118, 210, 0.12)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
            onClick={() => {
              setSelectedPageKey(key);
              setSelectedComponentPath(null);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {isHome ? (
                <HomeFilled fontSize="small" />
              ) : level > 0 ? (
                <SubdirectoryArrowRight
                  fontSize="small"
                  sx={{ color: '#757575' }}
                />
              ) : (
                <Description fontSize="small" sx={{ color: '#757575' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={page.menutitle || key}
              primaryTypographyProps={{ fontSize: '0.9rem' }}
            />
          </ListItemButton>
        </ListItem>
        {hasSubpages && (
          <List disablePadding>
            {Object.entries(page.subpages!).map(([subKey, subPage]) =>
              renderPageItem(subKey, subPage, level + 1),
            )}
          </List>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 128px)' }}>
      {/* Side Panel */}
      <Paper
        elevation={0}
        sx={{
          width: 280,
          borderRight: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        {/* PAGES Section */}
        <Box>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
              cursor: 'pointer',
            }}
            onClick={() => setPagesExpanded(!pagesExpanded)}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              PAGES
            </Typography>
            <IconButton size="small">
              {pagesExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={pagesExpanded}>
            <List disablePadding>
              {config &&
                Object.entries(config.pages).map(([key, page]) =>
                  renderPageItem(key, page),
                )}

              {isAdmin && (
                <ListItemButton sx={{ pl: 2, color: '#1976d2', mt: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                    <Add fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="ADD PAGE"
                    primaryTypographyProps={{
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                    }}
                  />
                </ListItemButton>
              )}
            </List>
          </Collapse>
        </Box>

        <Divider />

        {/* ADD CONTENT Section */}
        {isAdmin && (
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#fff',
                cursor: 'pointer',
              }}
              onClick={() => setAddContentExpanded(!addContentExpanded)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                ADD CONTENT
              </Typography>
              <IconButton size="small">
                {addContentExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={addContentExpanded}>
              <Box sx={{ p: 2, pt: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
                >
                  Top banner
                </Typography>
                {renderAddContentButton('HERO')}
                {renderAddContentButton('PAGEHEADER')}

                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 1, mt: 2, fontWeight: 'bold' }}
                >
                  Space
                </Typography>
                {renderAddContentButton('DIVIDER')}

                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 1, mt: 2, fontWeight: 'bold' }}
                >
                  Container
                </Typography>
                {renderAddContentButton('CONTAINER')}

                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 1, mt: 2, fontWeight: 'bold' }}
                >
                  Items
                </Typography>
                {renderAddContentButton('TEXTBOX')}
                {renderAddContentButton('IMAGE')}
                {renderAddContentButton('GALLERIA')}
                {renderAddContentButton('CAROUSEL')}
                {renderAddContentButton('INGRESS')}
                {renderAddContentButton('SIDETITLE')}
                {renderAddContentButton('VIDEO')}
                {renderAddContentButton('IFRAME')}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* SEO & AI Section */}
        {selectedPageKey && (
          <>
            <Divider />
            <Box>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#fff',
                  cursor: 'pointer',
                }}
                onClick={() => setSeoExpanded(!seoExpanded)}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  SEO & AI
                </Typography>
                <IconButton size="small">
                  {seoExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={seoExpanded}>
                <SeoEditor
                  seo={selectedPageConfig?.seo}
                  onChange={handleSeoChange}
                />
              </Collapse>
            </Box>
          </>
        )}
      </Paper>

      {/* Main Content Area */}
      <ThemeProvider theme={siteTheme}>
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            bgcolor: 'background.default',
            backgroundImage: config?.theme?.backgroundImage
              ? `url(${config.theme.backgroundImage})`
              : 'none',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
          }}
          onClick={() => setSelectedComponentPath(null)}
        >
          {selectedPageConfig ? (
            <Box
              sx={{
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {selectedPageConfig.components?.map((component, index) =>
                renderComponent(component, index),
              )}
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" color="textSecondary">
                Select a page to edit content
              </Typography>
            </Box>
          )}
        </Box>
      </ThemeProvider>
      {/* Component Action Menu */}
      <Popper
        open={Boolean(anchorEl && selectedComponentPath && isAdmin)}
        anchorEl={anchorEl}
        placement="top-end"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <Paper
          elevation={4}
          sx={{
            display: 'flex',
            bgcolor: '#fff',
            borderRadius: 1,
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={() =>
              selectedComponentPath &&
              handleComponentAction('moveUp', selectedComponentPath)
            }
            title="Move Up"
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() =>
              selectedComponentPath &&
              handleComponentAction('moveDown', selectedComponentPath)
            }
            title="Move Down"
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <IconButton
            size="small"
            onClick={() =>
              selectedComponentPath &&
              handleComponentAction('duplicate', selectedComponentPath)
            }
            title="Duplicate"
          >
            <ContentCopy fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() =>
              selectedComponentPath &&
              handleComponentAction('delete', selectedComponentPath)
            }
            title="Delete"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Paper>
      </Popper>

      {/* Page Action Menu */}
      <Menu
        anchorEl={pageMenuAnchorEl}
        open={Boolean(pageMenuAnchorEl)}
        onClose={handlePageMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handlePageRename}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        {isAdmin && (
          <>
            <MenuItem onClick={() => handlePageMove('up')}>
              <ListItemIcon>
                <ArrowUpward fontSize="small" />
              </ListItemIcon>
              <ListItemText>Move Up</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handlePageMove('down')}>
              <ListItemIcon>
                <ArrowDownward fontSize="small" />
              </ListItemIcon>
              <ListItemText>Move Down</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handlePageDuplicate}>
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePageRemove}>
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (confirmAction) confirmAction();
          setConfirmOpen(false);
        }}
        title={confirmTitle}
        content={confirmContent}
      />
    </Box>
  );
};

export default PageEditor;
